import type { ComponentType, ReactNode } from "react";
import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { Button } from "@similarweb/ui-components";
import { i18nFilter } from "app/filters/i18nFilter";
import type { ICustomSegmentAccount, ICustomSegmentsGroup } from "app/services/segments/segmentsApiService";
import { BoxContainer } from "pro-features/pages/conversion/components/ConversionScatterChart/StyledComponents";
import { SegmentsAnalysisContainer } from "pro-features/pages/segments/StyledComponents";
import {
    EmptyStateContainer,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateSubTitle,
    EmptyStateActionButtons,
} from "./StyledComponents";
import { proNavigator } from "single-spa/react-app/ProNavigator";

interface ISegmentsAnalysisAdvancedEmptyWrapper {
    children: ReactNode;
    params: { mode: string; id: string };
    allSegments: ICustomSegmentAccount[];
    groups: ICustomSegmentsGroup[];
}

const segmentEmptyStateTexts = {
    single: {
        main: "segments.nodata.advanced.single.main",
        sub: "segments.nodata.advanced.single.sub",
    },
    group: {
        main: "segments.nodata.advanced.group.main",
        sub: "segments.nodata.advanced.group.sub",
    },
};

const SegmentsAnalysisAdvancedEmptyWrapperComponent = (props: ISegmentsAnalysisAdvancedEmptyWrapper) => {
    const { children, params } = props;

    const { i18n, swNavigator } = useMemo(
        () => ({
            i18n: i18nFilter(),
            swNavigator: proNavigator,
        }),
        [],
    );

    const [updatingInProcess, setUpdatingInProcess] = React.useState(false);

    const onBackClick = useCallback(() => {
        const currentModule = swNavigator.getCurrentModule();
        const segmentsHomepage = `${currentModule}-homepage`;
        swNavigator.go(segmentsHomepage);
    }, []);

    return updatingInProcess ? (
        <SegmentsAnalysisContainer>
            <BoxContainer>
                <EmptyStateContainer>
                    <EmptyStateIcon />
                    <EmptyStateTitle>{i18n(segmentEmptyStateTexts[params.mode].main)}</EmptyStateTitle>
                    <EmptyStateSubTitle>{i18n(segmentEmptyStateTexts[params.mode].sub)}</EmptyStateSubTitle>
                    <EmptyStateActionButtons>
                        <Button
                            type="flat"
                            onClick={onBackClick}
                        >
                            {i18n("segments.nodata.advanced.button.goBack")}
                        </Button>
                    </EmptyStateActionButtons>
                </EmptyStateContainer>
            </BoxContainer>
        </SegmentsAnalysisContainer>
    ) : (
        children
    );
};

const mapStateToProps = (store) => {
    const {
        segmentsModule: { customSegmentsMeta },
        routing: { params },
    } = store;
    return {
        allSegments: customSegmentsMeta?.AccountSegments,
        groups: customSegmentsMeta?.SegmentGroups,
        params,
    };
};

const SegmentsAnalysisAdvancedEmptyWrapper = connect(mapStateToProps)(
    SegmentsAnalysisAdvancedEmptyWrapperComponent as ComponentType<any>,
);

export default SegmentsAnalysisAdvancedEmptyWrapper;
